import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  CirclePlus,
  Copy,
  Play,
  Trash2,
  Wrench,
  CloudCheck,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormNotesPatientFilesComponents } from "@/components/practitioner/FormNotesPatientFiles";
import { SingleSessionResultsPatientFile } from "@/components/practitioner/SingleSessionResultsPatientFile";
import axios from "axios";
import { toast } from "react-toastify";
import { usePatientContext } from "@/hooks/usePatientContext";
import { capitalize } from "@/utils/capitalize";
import { saveResultsModules } from "@/utils/saveResultsModules";
import { realizeSessionEvolveSelection } from "@/utils/realizeSessionEvolveSelection";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface Session {
  id: number;
  sujet: string;
  notes: string;
  status: "PREVUE" | "REALISEE" | "EN_COURS";
  order: number;
  sessionDate: string;
  fiable: boolean;
  resultsBilan: string | null; // Global success rate or similar
  resultsZF: string | null; // Global success rate or similar
  resultsZPCCF: string | null; // JSON string with success and failure counts
  resultsZPFCF: string | null; // JSON string with success and failure counts
  resultsZPL: string | null; // Score out of 10
  resultsOB: string | null; // Score out of 10
  resultsOT: string | null; // Score out of 10
  resultsPVJ: string | null; // Score out of 10
  resultsPVS: string | null; // Score out of 10
  resultsZPI: string | null; // Score out of 10
  resultsBilanLecture: string | null; // Reading time in seconds
  resultsExploration: string | null; // JSON format with trial results
  type: string[];
  patientId: number;
  sessionType: "BILAN" | "REEDUCATION";
  accountUsed: "PATIENT" | "CLINIQUE";
  screenUsed: string | null;
}

// Rendu du popup d'aperçu des types en portal pour éviter le clipping dans le tableau
const TypesHoverPreview: React.FC<{
  types: string[];
  colorBgExercice: (sessionType: string) => string;
}> = ({ types, colorBgExercice }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  const [popupHover, setPopupHover] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const visible = hover || popupHover;

  useEffect(() => {
    if (hover && anchorRef.current) {
      setRect(anchorRef.current.getBoundingClientRect());
    }
  }, [hover]);

  const first = Array.isArray(types) && types.length > 0 ? types[0] : null;
  const extra = Array.isArray(types) && types.length > 1 ? types.length - 1 : 0;

  const popup =
    visible && rect
      ? createPortal(
          <div
            onMouseEnter={() => setPopupHover(true)}
            onMouseLeave={() => setPopupHover(false)}
            style={{
              position: "fixed",
              top: Math.min(rect.bottom + 8, window.innerHeight - 16),
              left: Math.min(rect.left, window.innerWidth - 340),
              zIndex: 9999,
            }}
            className="bg-white border border-gray-300 p-2 rounded-md shadow-lg"
          >
            <div className="grid grid-cols-[repeat(3,auto)] gap-2">
              {Array.isArray(types) &&
                types.map((type, index) => (
                  <span
                    key={index}
                    className={`block ${colorBgExercice(
                      type
                    )} border border-gray-300 rounded px-2 py-1 text-sm`}
                  >
                    {capitalize(type)}
                  </span>
                ))}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div
        ref={anchorRef}
        className="flex items-center"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {first ? (
          <>
            <span
              className={`inline-block ${colorBgExercice(
                first
              )} border border-gray-300 rounded px-2 py-1 mr-1`}
            >
              {capitalize(first)}
            </span>
            {extra > 0 && (
              <span className="inline-block border border-gray-300 rounded px-2 py-1">
                +{extra}
              </span>
            )}
          </>
        ) : (
          <span className="inline-block bg-gray-200 border border-gray-300 rounded px-2 py-1">
            N/A
          </span>
        )}
      </div>
      {popup}
    </>
  );
};

const PatientFile: React.FC = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientInfo, setPatientInfo } = usePatientContext();

  const [seancesPrevue, setSeancesPrevue] = useState<Session[]>([]);
  const [seancesRealisee, setSeancesRealisee] = useState<Session[]>([]);
  const [seancesEnCours, setSeancesEnCours] = useState<Session[]>([]);

  const fetchPatientInfo = async () => {
    if (!patientId) return;

    // Vérifie si le patientId est déjà chargé dans le contexte
    if (Number(patientId) === patientInfo?.patientId) return;

    try {
      const response = await axios.get(
        `/practitioner/patientShort/${patientId}`
      );
      const patientData = response.data;
      console.log("patientDataShort", patientData);
      setPatientInfo({
        firstName: patientData.firstName,
        lastName: patientData.lastName,
        patientId: patientData.id,
      }); // Met à jour les informations du patient
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des informations du patient :",
        error
      );
    }
  };

  if (
    patientId !== undefined &&
    patientInfo?.patientId !== undefined &&
    Number(patientId) !== patientInfo.patientId
  ) {
    fetchPatientInfo();
  }

  useEffect(() => {
    axios
      .get(`/practitioner/sessions/${patientId}`)
      .then((response) => {
        const prevue = response.data.filter(
          (seance: Session) => seance.status === "PREVUE"
        );
        const realisee = response.data.filter(
          (seance: Session) => seance.status === "REALISEE"
        );
        const enCours = response.data.filter(
          (seance: Session) => seance.status === "EN_COURS"
        );
        setSeancesPrevue(prevue);
        setSeancesRealisee(realisee);
        setSeancesEnCours(enCours);
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des données:", error);
      });
  }, [patientId]);

  const prevOrdersRef = useRef<number[]>([]);

  // Helper pour comparer deux tableaux de nombres
  const arraysEqual = (a: number[], b: number[]) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  // Fonction qui exécute le tri, le diff, et les appels PUT
  const updateSeanceOrders = async (sessions: Session[]) => {
    // 1) Tri par ordre croissant
    const sortedSeances = sessions.slice().sort((a, b) => a.order - b.order);

    // 2) Ré-indexation pour repartir à 1, 2, 3…
    const updatedSeances = sortedSeances.map((seance, idx) => ({
      ...seance,
      order: idx + 1,
    }));

    // 3) Filtrer celles dont l’ordre a changé
    const seancesToUpdate = updatedSeances.filter(
      (seance, idx) => seance.order !== sessions[idx].order
    );

    if (seancesToUpdate.length === 0) {
      // Rien à faire
      return;
    }

    try {
      // 4) Envoi des PUT en parallèle
      await Promise.all(
        seancesToUpdate.map((seance) =>
          axios.put(`/practitioner/sessionorder/${seance.id}`, {
            newOrder: seance.order,
          })
        )
      );
      // 5) Mise à jour de l’état local avec les nouveaux ordres
      setSeancesPrevue(updatedSeances);
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l’ordre :", error);
    }
  };

  // useEffect qui ne réagit qu’aux changements de l’ordre
  const seancesPrevueOrdersKey = seancesPrevue.map((s) => s.order).join(",");
  useEffect(() => {
    const currentOrders = seancesPrevue.map((s) => s.order);
    if (arraysEqual(prevOrdersRef.current, currentOrders)) return;
    prevOrdersRef.current = currentOrders;
    updateSeanceOrders(seancesPrevue);
  }, [seancesPrevueOrdersKey, seancesPrevue]);

  useEffect(() => {
    interface Session {
      id: number;
      status: string;
      order: number;
    }

    function arraysEqual(a: Session[], b: Session[]): boolean {
      if (a.length !== b.length) {
        return false;
      }

      for (let i = 0; i < a.length; i++) {
        if (a[i].order !== b[i].order) {
          return false;
        }
      }

      return true;
    }
    const sortedSeances = seancesPrevue
      .slice()
      .sort((a, b) => a.order - b.order);

    // Vérifier si le tableau a changé
    const hasChanged = !arraysEqual(sortedSeances, seancesPrevue);

    if (hasChanged) {
      // Mettre à jour le tableau seancesPrevue avec les objets mis à jour
      const updatedSeances = sortedSeances.map((seance, idx) => ({
        ...seance,
        order: idx + 1, // Assurez-vous que l'ordre commence à 1
      }));

      setSeancesPrevue(updatedSeances);
    }
  }, [seancesPrevue]);

  const moveSeance = (index: number, direction: number) => {
    const newPosition = index + direction;
    if (newPosition < 0 || newPosition >= seancesPrevue.length) return;
    const newSeances = Array.from(seancesPrevue);
    const itemMoved = newSeances.splice(index, 1)[0];
    newSeances.splice(newPosition, 0, itemMoved);

    // Mise à jour de l'ordre après le déplacement
    const updatedSeances = newSeances.map((seance, idx) => ({
      ...seance,
      order: idx + 1, // Assurez-vous que l'ordre commence à 1
    }));

    // Find the IDs of the sessions that were modified
    const idSession1 = updatedSeances[index].id;
    const idSession2 = updatedSeances[newPosition].id;

    // Update the orders with the correct new orders
    try {
      axios.put(`/practitioner/sessionorder/${idSession1}`, {
        newOrder: updatedSeances[index].order,
      });
      axios.put(`/practitioner/sessionorder/${idSession2}`, {
        newOrder: updatedSeances[newPosition].order,
      });
      setSeancesPrevue(updatedSeances);
    } catch (error) {
      console.error("Error updating session order:", error);
    }
  };

  const deleteSeance = (index: number, seanceType: "PREVUE" | "EN_COURS") => {
    const sessionType =
      seanceType === "PREVUE"
        ? seancesPrevue
        : seanceType === "EN_COURS"
        ? seancesEnCours
        : undefined;
    if (!sessionType) return;
    axios
      .delete(`/practitioner/session/${sessionType[index].id}`)
      .then(() => {
        toast.success("La séance a bien été supprimée");
      })
      .catch((error) => {
        console.error("Erreur lors de la suppression de la séance:", error);
        toast.error("Erreur lors de la suppression de la séance", {
          position: "top-center",
          autoClose: 2000,
        });
      });
    const updatedSeances = sessionType.filter((_, i) => i !== index);

    for (let i = index; i < updatedSeances.length; i++) {
      updatedSeances[i].order -= 1;
    }
    if (seanceType === "PREVUE") {
      setSeancesPrevue(updatedSeances);
    } else if (seanceType === "EN_COURS") {
      setSeancesEnCours(updatedSeances);
    }
  };

  const duplicateSeance = (index: number) => {
    axios
      .post(`/practitioner/session/${seancesPrevue[index].id}/duplicate`)
      .then((response) => {
        toast.success("La séance a bien été dupliquée");
        // console.log("duplicatedSeance", response.data);
        const newSeance = {
          ...seancesPrevue[index],
          id: response.data, // Use the ID returned by the server
          order: seancesPrevue.length + 1, // Ajout à la fin, donc l'ordre est la taille actuelle du tableau + 1
        };

        setSeancesPrevue([...seancesPrevue, newSeance]);
      })
      .catch((error) => {
        console.error("Erreur lors de la duplication de la séance:", error);
        toast.error("Erreur lors de la duplication de la séance", {
          position: "top-center",
          autoClose: 2000,
        });
      });
  };

  const handleNavigateToParam = (seanceId: number, sessionType: string) => {
    navigate(`/praticien/${patientId}/${seanceId}`, { state: { sessionType } });
  };

  const handleSessionLaunch = (seanceId: number, sessionType: string) => {
    if (!sessionType) {
      toast.error("Type de séance non défini", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    if (sessionType === "BILAN") {
      navigate(`/modules/${patientId}/${seanceId}/bilan`, {
        state: { sessionType: "BILAN" },
      });
    } else if (sessionType === "REEDUCATION") {
      navigate(`/modules/${patientId}/${seanceId}/reeducation`, {
        state: { sessionType: "REEDUCATION" },
      });
    }
  };

  const endSession = (
    sessionId: string,
    sessionType: "BILAN" | "REEDUCATION"
  ) => {
    if (!sessionId || !sessionType || !patientId) {
      toast.error("ID de séance ou type de séance manquant", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    const fieldsToUpdate = [{ field: "status", value: "REALISEE" }];
    try {
      saveResultsModules(fieldsToUpdate, sessionId, "PRACTITIONER").catch(
        (error) => {
          console.error(
            "An error occurred while updating session status:",
            error
          );
        }
      );
      realizeSessionEvolveSelection(Number(sessionId), patientId, sessionType);
    } catch (error) {
      console.error(
        "An error occurred while preparing session status update:",
        error
      );
    }
  };

  const colorBgExercice = (sessionType: string) => {
    switch (sessionType) {
      case "ZF":
        return "bg-module-champ-row";
      case "ZPCCF":
        return "bg-module-champ-row";
      case "ZPFCF":
        return "bg-module-champ-row";
      case "ZPL":
        return "bg-module-champ-row";
      case "ZPI":
        return "bg-module-champ-row";
      case "OB":
        return "bg-module-optocinetique-row";
      case "OT":
        return "bg-module-optocinetique-row";
      case "PVJ":
        return "bg-module-poursuite-row";
      case "PVS":
        return "bg-module-poursuite-row";
      case "BILAN":
        return "bg-module-champ-row";
      case "BILAN_Lecture":
        return "bg-module-optocinétique-row";
      case "BILAN_Exploration":
        return "bg-module-poursuite-row";
      default:
        return "bg-gray-200";
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex justify-around w-full items-center mb-6 mt-6">
        <Button
          className=" w-1/6"
          variant="outline"
          onClick={() => navigate(`/praticien/${patientId}/resultats`)}
        >
          <BarChart3 className="mr-2" />
          Résultats
        </Button>
        <Card
          className=" w-3/6 cursor-pointer"
          onClick={() => navigate(`/praticien/${patientId}/fiche`)}
        >
          <CardHeader>
            <CardTitle>
              {capitalize(patientInfo?.firstName || "")}{" "}
              {patientInfo?.lastName.toUpperCase()}
            </CardTitle>
            <CardDescription>
              Profil patient : informations sur le patient
            </CardDescription>
          </CardHeader>
        </Card>
        <div className="flex flex-col w-1/6 h-full gap-3">
          <Button
            className="w-full"
            type="button"
            onClick={() => navigate(`/praticien/${patientId}/programmation`)}
          >
            <CirclePlus className="mr-2" />
            Nouvelle Séance
          </Button>
          {/* <Button
            type="button"
            onClick={() => navigate(`/praticien/${patientId}/parametragecv`)}
          >
            <Grid3X3 className="h-4 w-4 mr-2" />
            Champ Visuel
          </Button> */}
        </div>
      </div>
      <div className="flex flex-row">
        <div className="w-1/2 p-6 ">
          <Card>
            <CardHeader>
              <CardTitle>Historique des Séances</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>#</TableHead>
                    {/* <TableHead>Thème</TableHead> */}
                    <TableHead>Exercice(s)</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Résultats</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="text-left">
                  {seancesRealisee
                    .slice()
                    .sort(
                      (a, b) =>
                        Number(new Date(b.sessionDate)) -
                        Number(new Date(a.sessionDate))
                    )
                    .map((seance) => (
                      <TableRow
                        key={seance.id}
                        className="cursor-pointer hover:bg-gray-100 h-[57px]"
                      >
                        <TableCell>
                          {new Date(seance.sessionDate).toLocaleDateString(
                            "fr-FR",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </TableCell>
                        <TableCell>
                          {" "}
                          <span
                            className={
                              seance.sessionType === "BILAN"
                                ? "bg-blue-400 text-yellow-400 inline-block border border-gray-300 rounded px-2 py-1 mr-1"
                                : "bg-blue-600 text-yellow-400 inline-block border border-gray-300 rounded px-2 py-1 mr-1"
                            }
                          >
                            {seance.sessionType === "BILAN" ? "B" : "R"}
                            {seance.order}
                          </span>
                        </TableCell>
                        {/* <TableCell>{seance.sujet}</TableCell> */}
                        <TableCell>
                          <TypesHoverPreview
                            types={Array.isArray(seance.type) ? seance.type : []}
                            colorBgExercice={colorBgExercice}
                          />
                        </TableCell>

                        <TableCell>
                          <FormNotesPatientFilesComponents
                            sujet={seance.sujet}
                            notes={seance.notes}
                            seanceId={seance.id}
                            fiable={seance.fiable}
                          />
                        </TableCell>
                        <TableCell className="flex justify-center">
                          <SingleSessionResultsPatientFile
                            session={seance}
                            fiable={seance.fiable}
                            accountUsed={seance.accountUsed}
                            sessionType={seance.sessionType}
                            order={seance.order}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
            <CardFooter className="flex justify-center"></CardFooter>
          </Card>
        </div>
        <div className="w-1/2 p-6">
          {seancesEnCours.length > 0 && (
            <Card className="mb-12">
              <CardHeader>
                <CardTitle>Séance En Cours</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <TooltipProvider>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Exercice(s)</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Résultats</TableHead>
                        <TableHead>Editer</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="text-left">
                      {seancesEnCours.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-4">
                            Aucune séance en cours
                          </TableCell>
                        </TableRow>
                      )}
                      {seancesEnCours
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map((seance, index) => (
                          <TableRow
                            key={seance.id}
                            className="cursor-pointer hover:bg-gray-100 h-[57px]"
                          >
                            <TableCell>
                              {new Date(seance.sessionDate).toLocaleDateString(
                                "fr-FR",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                }
                              )}
                            </TableCell>
                            <TableCell>
                              <TypesHoverPreview
                                types={Array.isArray(seance.type) ? seance.type : []}
                                colorBgExercice={colorBgExercice}
                              />
                            </TableCell>
                            <TableCell>
                              <FormNotesPatientFilesComponents
                                sujet={seance.sujet}
                                notes={seance.notes}
                                seanceId={seance.id}
                                fiable={seance.fiable}
                              />
                            </TableCell>
                            <TableCell>
                              <SingleSessionResultsPatientFile
                                session={seance}
                                fiable={seance.fiable}
                                accountUsed={seance.accountUsed}
                                sessionType={seance.sessionType}
                                order={seance.order}
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    onClick={() =>
                                      handleNavigateToParam(
                                        seance.id,
                                        seance.sessionType
                                      )
                                    }
                                  >
                                    <Wrench className="h-4 w-4" />
                                    <span className="sr-only">Modifier</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Modifier</TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    className="bg-yellow-100"
                                    variant="outline"
                                    onClick={() =>
                                      handleSessionLaunch(
                                        seance.id,
                                        seance.sessionType
                                      )
                                    }
                                    aria-label="Reprendre la séance"
                                  >
                                    <Play className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Reprendre</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    aria-label="Marquer comme réalisée"
                                    onClick={async () => {
                                      try {
                                        endSession(
                                          seance.id.toString(),
                                          seance.sessionType
                                        );
                                        toast.success(
                                          "Séance passée en réalisée"
                                        );
                                        setSeancesEnCours((prev) =>
                                          prev.filter((s) => s.id !== seance.id)
                                        );
                                        setSeancesRealisee((prev) => [
                                          ...prev,
                                          {
                                            ...seance,
                                            status: "REALISEE" as const,
                                          },
                                        ]);
                                      } catch (e) {
                                        console.error(e);
                                        toast.error(
                                          "Échec de la mise à jour du statut"
                                        );
                                      }
                                    }}
                                  >
                                    <CloudCheck className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Clôturer</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    onClick={() =>
                                      deleteSeance(index, "EN_COURS")
                                    }
                                    aria-label="Supprimer"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Supprimer</TooltipContent>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TooltipProvider>
              </CardContent>
              <CardFooter className="flex justify-center"></CardFooter>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Futures Séances</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>ID</TableHead>
                    {/* <TableHead>Thème</TableHead> */}
                    <TableHead>Exercices</TableHead>
                    <TableHead>Editer</TableHead>
                    <TableHead>Actions</TableHead>
                    <TableHead>Demarrer l'exercice</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="text-left">
                  {seancesPrevue.map((seance, index) => (
                    <TableRow
                      key={seance.id}
                      className="cursor-pointer hover:bg-gray-100 h-[57px]"
                    >
                      <TableCell>{seance.order}</TableCell>
                      <TableCell>{seance.id}</TableCell>
                      {/* <TableCell>{seance.sujet}</TableCell> */}
                      <TableCell>
                        <TypesHoverPreview
                          types={Array.isArray(seance.type) ? seance.type : []}
                          colorBgExercice={colorBgExercice}
                        />
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="outline"
                          onClick={() =>
                            handleNavigateToParam(seance.id, seance.sessionType)
                          }
                        >
                          <span className="sr-only">Modifier</span>
                          <Wrench className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          onClick={() => moveSeance(index, -1)}
                          aria-label="Monter"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => moveSeance(index, 1)}
                          aria-label="Descendre"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => deleteSeance(index, "PREVUE")}
                          aria-label="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => duplicateSeance(index)}
                          aria-label="Dupliquer"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell>
                        {seance.order === 1 ? (
                          <Button
                            variant="outline"
                            className="bg-green-400"
                            onClick={() =>
                              handleSessionLaunch(seance.id, seance.sessionType)
                            }
                          >
                            Démarrage
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
            <CardFooter className="flex justify-center"></CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PatientFile;
